import React, { Suspense, useEffect, useMemo, useState } from "react";
import ReactGA from "react-ga";
import { Helmet } from "react-helmet";
import { I18nextProvider, useTranslation } from "react-i18next";
import Modal from "react-modal";
import { useLocation } from "react-router-dom";
import styled, { ThemeProvider } from "styled-components";
import { getConfig } from "./api";
import { ErrorBoundary, Layout, Loader } from "./components";
import { ConfigProvider, useConfig } from "./contexts";
import createI18N from "./i18n";
import Pages from "./pages";
import { Config } from "./types";
import GlobalStyles from "./components/GlobalStyles";
import { getThemeForAgentName } from "./utils/getThemeForAgentName";

Modal.setAppElement("#root");

const App: React.FC = () => {
  // config
  const [config, setConfig] = useState<Config>();
  useEffect(() => {
    (async () => {
      const config = await getConfig();
      setConfig(config);
    })();
  }, [setConfig]);

  // i18n
  const i18n = useMemo(() => {
    if (config) {
      return createI18N(config.defaultLang);
    }
  }, [config]);

  // google analitycs
  const location = useLocation();
  useEffect(() => {
    if (config) {
      ReactGA.initialize(config.googleAnalyticsKey);
      ReactGA.pageview(`${location.pathname}${location.search}`);
    }
  }, [config, location.pathname, location.search]);

  const theme = useMemo(() => {
    if (config) {
      return getThemeForAgentName(config.name, config.template);
    }
  }, [config]);

  return config && i18n && theme ? (
    <ConfigProvider config={config}>
      <I18nextProvider i18n={i18n}>
        <Suspense
          fallback={
            <LoaderContainer>
              <Loader />
            </LoaderContainer>
          }
        >
          <AppHead />
          <ThemeProvider theme={theme}>
            <Layout agentName={config.name} layout={config.template}>
              <ErrorBoundary>
                <Pages />
              </ErrorBoundary>
            </Layout>
            <GlobalStyles />
          </ThemeProvider>
        </Suspense>
      </I18nextProvider>
    </ConfigProvider>
  ) : (
    <LoaderContainer>
      <Loader />
    </LoaderContainer>
  );
};

const LoaderContainer = styled.div`
  align-items: center;
  display: flex;
  height: 100vh;
  justify-content: center;
  width: 100vw;
  box-sizing: border-box;
  padding-top: 3.75rem;
`;

const AppHead: React.FC = () => {
  const { t } = useTranslation();
  const { name } = useConfig();

  const title = useMemo(() => {
    const title = t("page_title", {
      agent: { name },
      interpolation: { prefix: "{", suffix: "}" },
    });
    return title !== "page_title" ? title : name;
  }, [name, t]);

  return (
    <Helmet>
      <link rel="icon" href={`https://media.${name.toLowerCase()}/favicon.ico`} />
      <title>{title}</title>
    </Helmet>
  );
};

export default App;
